/*
 * TrackShareModal — Generic per-track share sheet.
 *
 * Renders a live 1080×1350 preview + the unified ShareChannels row.
 * Any surface that shares a track (Track Page, dashboard cards,
 * search results, feed rows, etc.) should open THIS modal — no
 * custom implementations.
 *
 * Props:
 *   open, onClose
 *   track:   { submission_id, track_title, cover_url, cover_art, artist_name, artist_handle, updated_at }
 *   artist:  { profile_photo, picture, artist_name, name }
 *   shareUrl (optional — computed if not passed)
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { bakeAll } from "@/lib/shareExport";
import { resolveShareCover, resolveShareArtistPhoto } from "@/lib/mediaUrls";
import ShareService from "@/lib/ShareService";
import ShareChannels from "@/components/share/ShareChannels";
import TrackShareCard from "@/components/share/TrackShareCard";

const TrackShareModal = ({ open, onClose, track, artist, shareUrl, currentUser }) => {
  const cardRef = useRef(null);
  const [baked, setBaked] = useState({ cover: null, avatar: null });
  const [blob, setBlob] = useState(null);
  const [generating, setGenerating] = useState(true);
  // Preset selector — reuses the same TrackShareCard render path;
  // switches only the frame size so the Story/Reel-ready 1080×1920
  // asset is a single click away from the existing modal. No new
  // Share Studio, no duplicated UI.
  const [preset, setPreset] = useState("square");
  // Feb 2026 — dynamic preview scale. The wrapper is aspectRatio-locked
  // to the card dims, so the correct fill scale is wrapper.clientWidth
  // / 1080. Previously fixed at 0.29/0.20 which under-filled the frame
  // on any modal wider than ~373px and produced a spurious blank band
  // below the composition.
  const previewWrapRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.29);
  useLayoutEffect(() => {
    const el = previewWrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const recompute = () => {
      const w = el.clientWidth;
      if (w > 0) setPreviewScale(w / 1080);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [preset, open]);

  const submission_id = track?.submission_id;
  const artist_handle = artist?.artist_name || artist?.name || track?.artist_name || "";

  // Canonical URL — use the OG-rich backend share endpoint so previews
  // on WhatsApp / Facebook / X / etc. show cover + title + artist. Prefer
  // an explicit shareUrl override for milestone / feature contexts.
  const canonicalUrl = React.useMemo(() => {
    if (shareUrl) return shareUrl;
    if (!submission_id) return "";
    const ref = currentUser?.referral_code || currentUser?.artist_name || currentUser?.name;
    let u = `${BACKEND_URL}/api/share/track/${submission_id}`;
    if (ref) u += `?ref=${encodeURIComponent(ref)}`;
    return u;
  }, [shareUrl, submission_id, currentUser]);

  const cardData = React.useMemo(() => {
    // Feb 2026 reliability fix: pass the ENTIRE track + artist records
    // into the resolvers so their full field cascade (11 cover variants,
    // 8 avatar variants) is actually exercised. Previously the modal
    // cherry-picked ~5 keys per resolver and silently discarded valid
    // legacy fields (image_url, coverUrl, coverArt, trackCover, avatar,
    // artist_avatar, host_avatar, camelCase variants), causing tracks
    // whose media lived in those fields to render with empty artwork
    // and an empty gold avatar ring.
    const coverRecord = {
      ...(track || {}),
      updated_at: track?.cover_updated_at || track?.updated_at,
    };
    const avatarRecord = {
      ...(artist || {}),
      // Track-side fallbacks for artist photo (some legacy payloads
      // embedded the photo on the submission record rather than the
      // artist record). Only fill from track when artist is missing
      // the corresponding field.
      profile_photo: artist?.profile_photo || track?.user_profile_photo || track?.artist_photo,
      avatar_url: artist?.avatar_url || track?.user_avatar_url,
      artist_avatar: artist?.artist_avatar || track?.artist_avatar,
      picture: artist?.picture || track?.user_picture,
    };
    return {
      track_title: track?.track_title || "Untitled",
      artist_handle,
      cover: resolveShareCover(coverRecord),
      avatar: resolveShareArtistPhoto(avatarRecord),
      share_url: canonicalUrl,
    };
  }, [track, artist, artist_handle, canonicalUrl]);

  // Bake the images then export the offscreen node once the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setGenerating(true);
    setBlob(null);

    const run = async () => {
      const r = await bakeAll({ cover: cardData.cover, avatar: cardData.avatar });
      if (cancelled) return;
      setBaked({ cover: r.cover, avatar: r.avatar });

      // Yield for a full paint cycle before capturing the node — the
      // baked data URIs must land in the DOM first.
      await new Promise((res) => setTimeout(res, 0));
      await new Promise((res) => requestAnimationFrame(() => requestAnimationFrame(res)));
      try { if (document.fonts?.ready) await document.fonts.ready; } catch { /* noop */ }

      if (cancelled) return;
      const b = await ShareService.generate(cardRef.current);
      if (!cancelled) {
        setBlob(b);
        setGenerating(false);
        if (!b) toast.error("Couldn't render preview — Copy Link still works.");
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, cardData.cover, cardData.avatar, preset]);

  // Fire-and-forget share tracking on any successful channel action.
  const handleEvent = (channel, status) => {
    if (status === "shared" || status === "downloaded") {
      if (submission_id) {
        api.post(`/track/${submission_id}/share`, { channel }).catch(() => {});
      }
    }
  };

  const safeTitle = track?.track_title || "Untitled";
  const artistLabel = artist_handle || track?.artist_name || "RGM Artist";
  const shareText = `🎧 "${safeTitle}" by ${artistLabel} — new on RGM Studio`;
  const filename = `RGM-${(safeTitle || "track").replace(/[^a-z0-9-_ ]/gi, "").trim() || "share"}.png`;

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="max-w-md bg-[#120720] border-purple-900/60 text-white p-0"
        data-testid="track-share-modal"
      >
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="text-lg font-black">Share this track</DialogTitle>
          <DialogDescription className="text-xs text-purple-200/70">
            Post to any channel — the preview image and OG card match rgmstudio.org.
          </DialogDescription>
        </DialogHeader>

        {/* Visible preview at 45% scale */}
        <div className="px-5 pb-4">
          {/* Preset switcher — story / square / reel share to the
              same channels via the same ShareService pipeline. */}
          <div className="flex gap-2 mb-3" data-testid="track-share-preset-row">
            {[
              { id: "square", label: "Post 1:1" },
              { id: "story",  label: "Story 9:16" },
              { id: "reel",   label: "Reel 9:16" },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPreset(p.id)}
                data-testid={`track-share-preset-${p.id}`}
                className={
                  "text-xs font-semibold px-3 py-1.5 rounded-full transition-colors " +
                  (preset === p.id
                    ? "bg-purple-600 text-white"
                    : "bg-white/10 text-purple-200 hover:bg-white/15")
                }
              >
                {p.label}
              </button>
            ))}
            <span className="ml-auto text-[10px] text-purple-300/70 self-center">
              Story/Reel-ready asset · you complete the post in Instagram/Facebook
            </span>
          </div>
          <div ref={previewWrapRef} className="rounded-xl overflow-hidden border border-white/10 relative bg-black/50"
               style={{ aspectRatio: preset === "square" ? "1080 / 1350" : "1080 / 1920" }}>
            <div style={{
              transform: `scale(${previewScale})`,
              transformOrigin: "top left",
              width: 1080,
              height: preset === "square" ? 1350 : 1920,
              position: "absolute", top: 0, left: 0,
            }}>
              <TrackShareCard data={cardData} bakedCover={baked.cover} bakedAvatar={baked.avatar} preset={preset} />
            </div>
            {generating && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-xs text-purple-200 font-semibold"
                   data-testid="track-share-generating">
                Preparing image…
              </div>
            )}
          </div>
        </div>

        {/* Channel row */}
        <div className="px-5 pb-5">
          <ShareChannels
            blob={blob}
            filename={filename}
            title={`${safeTitle} — ${artistLabel}`}
            text={shareText}
            url={canonicalUrl}
            onEvent={handleEvent}
            testIdPrefix="track-share"
          />
          <p className="text-[11px] text-gray-500 mt-3">
            Native Share includes the image on supported devices. WhatsApp / X / Facebook pull the OG preview from RGM.
          </p>
        </div>

        {/* Offscreen full-size render used for the export blob. */}
        <div style={{
          position: "fixed", top: 0, left: -99999,
          opacity: 0.01, pointerEvents: "none",
        }} aria-hidden>
          <TrackShareCard ref={cardRef} data={cardData} bakedCover={baked.cover} bakedAvatar={baked.avatar} preset={preset} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrackShareModal;
